import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { DriverSearchField } from '../reports/DriverSearchField'
import { pollWhileVisible } from '../lib/pollWhileVisible'
import { fetchBusAlerts } from './useBusApi'
import type { BusAlert } from './types'

const inputCls =
  'rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] text-slate-700 ' +
  'focus:border-amber-400 focus:outline-none'

/** Bus-domain alerts (driving-behaviour -- speed, harsh brake, harsh accel -- plus
 *  battery-overheat) for one SPV's bus fleet. Detection + bus_alert table + this read
 *  API all already existed (tools/bus_harsh_driving.py/bus_overspeed.py, --spv NAGPUR
 *  cron, GET /api/bus/alerts) -- this component was the only missing piece; nothing on
 *  /api/bus/alerts consumed it before this. Same poll-not-push shape as
 *  busCongestion.ts's CONGESTION_POLL_MS, for the same reason: HARSH_BRAKE/HARSH_ACCEL/
 *  OVERSPEED are still a 15-minute batch job, not a stream. BATTERY_OVERHEAT is the
 *  exception, as of .claude/work/SPEC.md's Nagpur streaming unification -- it now
 *  writes into this same bus_alert table from quix-bus-driving (live), not
 *  tools/bus_overheat.py (retired batch CronJob, unscheduled but not deleted) -- so
 *  this panel's own 60s poll is the only latency BATTERY_OVERHEAT rows see here.
 *
 *  Live-tile TTL (.claude/work/SPEC.md): deliberately NOT applied here, audited and
 *  excluded like EIM's condition-based alerts, for a different reason -- this feed's
 *  detector is a 15-min CronJob over a 20-min lookback
 *  (infra/k8s/04-jobs.yaml:bus-harsh-driving-detect et al, "8-59/15 * * * *",
 *  tools/bus_harsh_driving.py --minutes 20) writing each row's `ts` as the ORIGINAL
 *  telemetry event time, not detection/insert time. Detection latency alone is
 *  uniformly distributed 0-15 minutes, so a 5-minute now-minus-ts TTL would hide most
 *  rows the instant they first become queryable and empty this panel almost always --
 *  the one surface in this ticket where the producer's own SLA is incompatible with a
 *  ts-based 5-minute window. Confirmed adversarially (ship Phase 4, round 1); do not
 *  reapply this filter here without first moving Nagpur's detectors onto a faster
 *  cadence or re-basing the TTL off an ingest/created_at column instead of `ts`. */
const ALERTS_POLL_MS = 60_000

const KIND_LABEL: Record<string, string> = {
  HARSH_BRAKE: 'Harsh brake',
  HARSH_ACCEL: 'Harsh accel',
  // Not a driving-behaviour event -- GET /api/bus/alerts' own docstring already
  // anticipates "and later, other bus-domain events" over this same table
  // (tools/bus_overheat.py, SPEC.md: MBMT+Nagpur battery-overheat alert). Labelled
  // here so it never renders as the raw enum string; still shown in this feed
  // since a sustained CRITICAL battery condition is exactly the kind of live,
  // unresolved bus-domain event this panel already exists to surface.
  BATTERY_OVERHEAT: 'Battery overheat',
}

const SEVERITY_BORDER: Record<string, string> = {
  CRITICAL: 'border-l-[#9F1239]',
  WARN: 'border-l-[#B45309]',
  INFO: 'border-l-[#0369A1]',
}

const SEVERITY_BADGE: Record<string, { label: string; bg: string; ink: string }> = {
  CRITICAL: { label: 'HIGH', bg: 'rgba(244,63,94,0.14)', ink: '#9F1239' },
  WARN: { label: 'MED', bg: 'rgba(245,158,11,0.14)', ink: '#B45309' },
  INFO: { label: 'LOW', bg: 'rgba(14,165,233,0.14)', ink: '#0369A1' },
}

const IST_CLOCK = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
})

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? (kind.startsWith('OVERSPEED') ? 'Overspeed' : kind)
}

export function BusAlertsFeed({ spv }: { spv: string }): JSX.Element {
  const [alerts, setAlerts] = useState<BusAlert[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [vehicleId, setVehicleId] = useState('')
  const [driverName, setDriverName] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  // Guards against ALERTS_POLL_MS firing a fresh fetch on top of one still in flight --
  // same bug class as BusOpsShell.tsx's own refreshInFlightRef.
  const pollInFlightRef = useRef(false)

  useEffect(() => {
    let alive = true
    const poll = () => {
      if (pollInFlightRef.current) return
      pollInFlightRef.current = true
      // sinceHours dropped once a dateFrom/dateTo filter is active -- the backend
      // treats dateFrom/dateTo as an absolute window REPLACING the relative one, not
      // additive (serving/app.py::_bus_alerts), so sending both would be misleading.
      const opts = dateFrom || dateTo
        ? { dateFrom, dateTo, vehicleId, driverName, limit: 200 }
        : { sinceHours: 24, vehicleId, driverName, limit: 200 }
      fetchBusAlerts(spv, opts)
        .then((rows) => {
          if (!alive) return
          setAlerts(rows)
          setError(null)
        })
        .catch((e: Error) => { if (alive) setError(e.message) })
        .finally(() => { if (alive) setLoaded(true); pollInFlightRef.current = false })
    }
    const stop = pollWhileVisible(poll, ALERTS_POLL_MS)
    return () => { alive = false; stop() }
  }, [spv, vehicleId, driverName, dateFrom, dateTo])

  const filters = (
    <div className="flex flex-wrap items-end gap-3 pb-2">
      <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
        vehicle
        <input
          type="search"
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          placeholder="MH-NAG-…"
          className={`${inputCls} w-[160px] font-mono normal-case`}
        />
      </label>
      <DriverSearchField
        value={driverName}
        onChange={setDriverName}
        rosterPath={`/api/bus/driver-roster?spv=${encodeURIComponent(spv)}`}
      />
      <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
        from
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
      </label>
      <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
        to
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
      </label>
    </div>
  )

  if (!loaded) {
    return (
      <div className="flex h-full min-h-0 flex-col p-3">
        {filters}
        <p className="p-4 text-[12px] text-[#475569]">Loading alerts…</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col p-3">
        {filters}
        <p className="p-4 text-[12px] text-[#9F1239]">{error}</p>
      </div>
    )
  }
  if (alerts.length === 0) {
    return (
      <div className="flex h-full min-h-0 flex-col p-3">
        {filters}
        <p className="p-4 text-[12px] text-[#475569]">No bus alerts match these filters.</p>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col p-3">
      {filters}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto rounded-[3px] border border-[rgba(15,23,42,0.14)] bg-[rgba(15,23,42,0.03)] p-3">
      {alerts.map((a) => {
        const badge = SEVERITY_BADGE[a.severity] ?? SEVERITY_BADGE.INFO!
        return (
          <div
            key={a.alert_id}
            className={clsx(
              'flex items-start gap-2.5 rounded-[3px] border border-[rgba(15,23,42,0.14)] border-l-[3px] bg-white px-3 py-2.5',
              SEVERITY_BORDER[a.severity] ?? SEVERITY_BORDER.INFO,
            )}
          >
            <div className="flex-1">
              <div className="text-[12px] font-medium text-[#0F172A]">
                {kindLabel(a.kind)} — {a.vehicle_id}
                {a.driver_name ? ` · ${a.driver_name}` : ''}
              </div>
              {a.message ? <div className="mt-0.5 text-[11px] text-[#475569]">{a.message}</div> : null}
              <div className="mt-0.5 font-mono text-[10px] text-[#94A3B8]">
                {IST_CLOCK.format(new Date(a.ts))} IST
              </div>
            </div>
            <span
              className="shrink-0 rounded-[2px] px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em]"
              style={{ background: badge.bg, color: badge.ink }}
            >
              {badge.label}
            </span>
          </div>
        )
      })}
      </div>
    </div>
  )
}
